import { useQuery } from '@tanstack/react-query';
import useNotifications from '~/context/Notifications';
import utils from '~/layouts/Admin/Admin.utils';
import useSessionPlQuery from '../useSessionPlQuery';

type AccountBase = {
  id: string;
  name: string;
  logo: string | null;
  permission_admin: number;
  description: string | null;
  allow_create_client: number | null;
  parent_client_id: number | null;
  isPartner: boolean;
};

type Account = AccountBase & {
  children?: AccountBase[];
};

type FlatAccount = Account;

type Client = Account;
type Partner = {
  id: string;
  name: string;
  logo: string | null;
  parent_partner_id: number | null;
  isPartner: true;
  children: Partner[];
};

type AccountsData = {
  flatAccounts: FlatAccount[];
  clients: Client[];
  partners: Partner[];
  clientsCount: number;
  partnersCount: number;
};

type AccountsResponse = {
  accounts: AccountsData;
  emptyAccounts: boolean;
} | null;

function useAccountsQuery(): ReturnType<typeof useQuery<AccountsResponse, Error>>;
function useAccountsQuery<TData>(
  select: (data: AccountsResponse) => TData,
): ReturnType<typeof useQuery<AccountsResponse, Error, TData>>;
function useAccountsQuery<TData>(select?: (data: AccountsResponse) => TData) {
  const { data: sessionData } = useSessionPlQuery();
  const { setTimedNotification } = useNotifications();

  return useQuery({
    queryKey: ['accounts.pl', sessionData?.id, sessionData?.isPartner],
    queryFn: async () => {
      const data = await utils.handleGetAccounts();
      if (!data) {
        setTimedNotification(
          'danger',
          'Error loading accounts data, please reload page. If the issue persists, contact support.',
          8000,
        );
      }
      return data;
    },
    select,
    refetchOnWindowFocus: false,
    // staleTime: 1000 * 60 * 30, // 30 minutes stale time
    enabled: !!sessionData?.id,
  });
}

export default useAccountsQuery;
